import React, { useEffect, useState, useCallback } from 'react';
import CallService from '../../services/callService';
import SoundService from '../../services/soundService';
import CallInterface from './CallInterface';
import CallNotification from './CallNotification';

interface CallManagerProps {
  userId: string;
  userName: string;
  onCallEnd?: () => void;
}

interface CallState {
  isIncoming: boolean;
  callerId?: string;
  callerName?: string;
}

interface ServiceCallState {
  isInCall: boolean;
  isMuted: boolean;
  isSpeakerOn: boolean;
  isVideoEnabled: boolean;
  remoteStream: MediaStream | null;
  localStream: MediaStream | null;
}

const CallManager: React.FC<CallManagerProps> = ({ userId, userName, onCallEnd }) => {
  const [callState, setCallState] = useState<CallState>({
    isIncoming: false
  });
  const [isInCall, setIsInCall] = useState(false);
  const [callStatus, setCallStatus] = useState<'idle' | 'calling' | 'ringing' | 'in-call' | 'declined' | 'cancelled' | 'missed'>('idle');
  const [callPayload, setCallPayload] = useState<any>(null);
  const [showMissed, setShowMissed] = useState(false);
  const missedCallTimer = React.useRef<NodeJS.Timeout | null>(null);
  const soundService = SoundService.getInstance();
  const isMounted = React.useRef(true);
  const [connectionStatus, setConnectionStatus] = useState('connecting');

  // Comprehensive cleanup function for all call end scenarios
  const performCallCleanup = useCallback(() => {
    if (!isMounted.current) return;
    
    console.log('[CallManager] Performing comprehensive call cleanup...');
    
    // Only stop sounds if we're not in a ringing state
    if (callStatus !== 'ringing') {
      soundService.stopAll();
    }
    
    // Clear any timers
    if (missedCallTimer.current) {
      clearTimeout(missedCallTimer.current);
      missedCallTimer.current = null;
    }

    // Only reset call state if we're not in a ringing state
    if (callStatus !== 'ringing') {
      setCallState({ isIncoming: false });
      setIsInCall(false);
      setCallStatus('idle');
      setCallPayload(null);
      setShowMissed(false);
      // Call the onCallEnd prop if provided
      if (typeof onCallEnd === 'function') {
        onCallEnd();
      }
    }

    // Ensure CallService performs its cleanup
    const callService = CallService.getInstance();
    callService.cleanupCall();

    console.log('[CallManager] Call cleanup completed');
  }, [callStatus, onCallEnd]);

  useEffect(() => {
    console.log('[CallManager] Mounting for userId:', userId);
    const callService = CallService.getInstance();
    if (userId) {
      console.log('[CallManager] setUserId called with:', userId);
      callService.setUserId(userId);
    }
    if (userName) {
      callService.setUserName(userName);
    }

    // Set up call state change listener
    callService.setCallStateChangeListener((state: ServiceCallState) => {
      if (!isMounted.current) return;
      setIsInCall(state.isInCall);
      
      // If call state shows not in call, ensure we clean up
      if (!state.isInCall && (callStatus === 'in-call' || isInCall)) {
        console.log('[CallManager] Call ended detected, cleaning up...');
        performCallCleanup();
      }
    });

    // Set up call status listener for new call flow
    callService.setCallStatusListener((status, payload) => {
      if (!isMounted.current) return;
      console.log('[CallManager] Call status changed:', status, payload);
      
      setCallStatus(status as any);
      setCallPayload(payload);
      
      if (status === 'ringing') {
        setCallState({
          isIncoming: true,
          callerId: payload?.callerId,
          callerName: payload?.callerName
        });
        // Play ringtone for incoming call
        soundService.playRingtone();
        // Start missed call timer (e.g., 30s)
        if (missedCallTimer.current) clearTimeout(missedCallTimer.current);
        missedCallTimer.current = setTimeout(() => {
          setCallStatus('missed');
          setShowMissed(true);
          soundService.stopRingtone();
          performCallCleanup();
        }, 30000); // 30 seconds
      } else if (status === 'calling') {
        setCallState({ isIncoming: false });
        // Play dial tone for outgoing call
        soundService.playDialTone();
      } else if (status === 'declined') {
        setCallState({ isIncoming: false });
        soundService.stopAll();
        soundService.playBeep();
        if (missedCallTimer.current) clearTimeout(missedCallTimer.current);
        // Perform cleanup after showing declined status briefly
        setTimeout(() => {
          performCallCleanup();
        }, 2000);
      } else if (status === 'cancelled') {
        setCallState({ isIncoming: false });
        soundService.stopAll();
        soundService.playBeep();
        if (missedCallTimer.current) clearTimeout(missedCallTimer.current);
        // Perform cleanup after showing cancelled status briefly
        setTimeout(() => {
          performCallCleanup();
        }, 2000);
      } else if (status === 'in-call') {
        setCallState({ isIncoming: false });
        soundService.stopAll();
        if (missedCallTimer.current) clearTimeout(missedCallTimer.current);
      } else if (status === 'missed') {
        setShowMissed(true);
        soundService.stopAll();
        soundService.playBeep();
        if (missedCallTimer.current) clearTimeout(missedCallTimer.current);
        performCallCleanup();
      } else if (status === 'idle') {
        // Only cleanup if we're not in a ringing state
        if (callStatus !== 'ringing') {
          performCallCleanup();
        }
      }
    });

    return () => {
      isMounted.current = false;
      console.log('[CallManager] Component unmounting for userId:', userId);
      callService.setCallStateChangeListener(() => {});
      callService.setCallStatusListener(() => {});
      performCallCleanup();
    };
  }, [userId, userName, performCallCleanup, callStatus, isInCall]);

  useEffect(() => {
    const callService = CallService.getInstance();
    callService.setConnectionStatusListener(setConnectionStatus);
    setConnectionStatus(callService.connectionStatus);
  }, [userId]);

  // Accept incoming call
  const handleAcceptCall = async () => {
    if (callState.callerId) {
      const callService = CallService.getInstance();
      try {
        soundService.stopRingtone();
        await callService.answerCall(callState.callerId);
        setCallState({ isIncoming: false });
        if (missedCallTimer.current) {
          clearTimeout(missedCallTimer.current);
          missedCallTimer.current = null;
        }
      } catch (error) {
        console.error('[CallManager] Error accepting call:', error);
        performCallCleanup();
      }
    }
  };

  // Decline incoming call
  const handleDeclineCall = () => {
    console.log('[CallManager] Declining call...');
    const callService = CallService.getInstance();
    if (callState.callerId && userName) {
      soundService.stopRingtone();
      callService.declineCall(callState.callerId, userName);
    }
    setCallState({ isIncoming: false });
    if (missedCallTimer.current) {
      clearTimeout(missedCallTimer.current);
      missedCallTimer.current = null;
    }
    // Cleanup will be handled by the status listener
  };

  // End call (in-call UI)
  const handleEndCall = () => {
    console.log('[CallManager] Ending call from interface...');
    const callService = CallService.getInstance();
    callService.endCall();
    performCallCleanup();
  };

  // Cancel outgoing call (while calling)
  const handleCancelCall = () => {
    console.log('[CallManager] Cancelling outgoing call...');
    const callService = CallService.getInstance();
    if (callPayload?.recipientId && callPayload.recipientId !== userId) {
      soundService.stopDialTone();
      callService.cancelCall(callPayload.recipientId);
    } else {
      console.log('[CallManager] Invalid recipient ID or self-call attempt');
    }
    performCallCleanup();
  };

  // Handler to close missed call notification
  const handleCloseMissed = () => {
    console.log('[CallManager] Closing missed call notification...');
    setShowMissed(false);
    performCallCleanup();
  };

  // Validate call recipient
  const validateCallRecipient = (recipientId: string | undefined) => {
    if (!recipientId || recipientId === userId) {
      console.log('[CallManager] Invalid recipient ID or self-call attempt');
      return false;
    }
    return true;
  };

  return (
    <>
      <div style={{position: 'fixed', top: 0, right: 0, background: '#eee', padding: 4, zIndex: 9999}}>
        Connection: {connectionStatus}
        <button 
          onClick={() => {
            const callService = CallService.getInstance();
            callService.debugConnection();
          }}
          style={{marginLeft: 8, padding: '2px 6px', fontSize: '10px'}}
        >
          Debug
        </button>
      </div>
      {/* In Call UI */}
      {isInCall && callStatus === 'in-call' && (
        <CallInterface
          recipientId={callState.callerId || callPayload?.recipientId || ''}
          recipientName={callState.callerName || callPayload?.recipientName || ''}
          onCallEnd={handleEndCall}
        />
      )}

      {/* Outgoing Calling UI */}
      {callStatus === 'calling' && validateCallRecipient(callPayload?.recipientId) && (
        <CallNotification
          callerName={callPayload?.recipientName || 'Calling...'}
          onAccept={() => {}}
          onDecline={handleCancelCall}
          isOutgoing
        />
      )}

      {/* Incoming Call UI */}
      {callStatus === 'ringing' && (
        <CallNotification
          callerName={callState.callerName || 'Incoming call...'}
          onAccept={handleAcceptCall}
          onDecline={handleDeclineCall}
          isOutgoing={false}
        />
      )}

      {/* Call Declined UI */}
      {callStatus === 'declined' && (
        <CallNotification
          callerName={callPayload?.calleeName || 'Call declined'}
          onAccept={() => performCallCleanup()}
          onDecline={() => performCallCleanup()}
          isDeclined
        />
      )}

      {/* Call Cancelled UI */}
      {callStatus === 'cancelled' && (
        <CallNotification
          callerName={'Call cancelled'}
          onAccept={() => performCallCleanup()}
          onDecline={() => performCallCleanup()}
          isCancelled
        />
      )}

      {/* Missed Call Notification */}
      {showMissed && callState.callerId && validateCallRecipient(callState.callerId) && (
        <CallNotification
          callerName={callState.callerName || 'Missed call'}
          onAccept={() => {}}
          onDecline={handleCloseMissed}
          isOutgoing={false}
          isMissed
        />
      )}
    </>
  );
};

export default CallManager;